/**
 * PhotographerContactModal - Contact form popup for photographer pages
 * 
 * Allows customers to send inquiries to photographers/360Lumina support
 */
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2, Check, Mail, Phone, User, Calendar, MessageSquare } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const INQUIRY_TYPES = [
  { value: "booking", label: "Booking Inquiry" },
  { value: "pricing", label: "Pricing Question" },
  { value: "availability", label: "Availability Check" },
  { value: "event_coverage", label: "Event Coverage" },
  { value: "portrait_session", label: "Portrait Session" },
  { value: "collaboration", label: "Collaboration" },
  { value: "other", label: "Other" }
];

const PhotographerContactModal = ({ 
  isOpen, 
  onClose, 
  photographerId, 
  photographerName,
  eventTypes = []
}) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    inquiry_type: "",
    event_type: "",
    event_date: "",
    message: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.inquiry_type || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact/photographer-inquiry`, {
        ...formData,
        photographer_id: photographerId
      });
      setSubmitted(true);
      toast.success("Your inquiry has been sent!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form state when closing
    setFormData({
      name: "",
      email: "",
      phone: "",
      inquiry_type: "",
      event_type: "",
      event_date: "",
      message: ""
    });
    setSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
            data-testid="contact-modal-backdrop"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div 
              className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              data-testid="contact-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-border">
                <div>
                  <h2 className="text-xl font-serif">Contact {photographerName}</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    We&apos;ll respond within 24-48 hours
                  </p>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 hover:bg-muted rounded-lg transition-colors"
                  data-testid="contact-modal-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-8"
                  >
                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-green-400" />
                    </div>
                    <h3 className="text-xl font-serif mb-2">Message Sent!</h3>
                    <p className="text-muted-foreground mb-6">
                      Thank you for reaching out. Our support team will get back to you within 24-48 hours.
                    </p>
                    <p className="text-sm text-muted-foreground mb-6">
                      A confirmation email has been sent to your inbox.
                    </p>
                    <Button onClick={handleClose} className="btn-primary">
                      Close
                    </Button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Name & Email */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          Name *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          placeholder="Your full name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          data-testid="contact-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          Email *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="you@example.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          data-testid="contact-email"
                        />
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={formData.phone}
                        onChange={handleInputChange}
                        data-testid="contact-phone"
                      />
                    </div>

                    {/* Inquiry Type */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-muted-foreground" />
                        Type of Inquiry *
                      </Label>
                      <Select
                        value={formData.inquiry_type}
                        onValueChange={(value) => handleSelectChange("inquiry_type", value)}
                      >
                        <SelectTrigger data-testid="contact-inquiry-type">
                          <SelectValue placeholder="Select inquiry type" />
                        </SelectTrigger>
                        <SelectContent>
                          {INQUIRY_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Event Type & Date - show only for relevant inquiry types */}
                    {["booking", "event_coverage", "availability"].includes(formData.inquiry_type) && (
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="event_type">Event Type</Label>
                          {eventTypes.length > 0 ? (
                            <Select
                              value={formData.event_type}
                              onValueChange={(value) => handleSelectChange("event_type", value)}
                            >
                              <SelectTrigger data-testid="contact-event-type">
                                <SelectValue placeholder="Select event type" />
                              </SelectTrigger>
                              <SelectContent>
                                {eventTypes.map((type) => (
                                  <SelectItem key={type} value={type}>
                                    {type}
                                  </SelectItem>
                                ))}
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              id="event_type"
                              name="event_type"
                              placeholder="e.g., Wedding, Sports, Portrait"
                              value={formData.event_type}
                              onChange={handleInputChange}
                              data-testid="contact-event-type"
                            />
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="event_date" className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            Event Date
                          </Label>
                          <Input
                            id="event_date"
                            name="event_date"
                            type="date"
                            value={formData.event_date}
                            onChange={handleInputChange}
                            data-testid="contact-event-date"
                          />
                        </div>
                      </div>
                    )}

                    {/* Message */}
                    <div className="space-y-2">
                      <Label htmlFor="message">Your Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="Tell us about your project, questions, or how we can help..."
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={4}
                        required
                        data-testid="contact-message"
                      />
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      className="w-full btn-primary"
                      disabled={loading}
                      data-testid="contact-submit"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-center text-muted-foreground">
                      By submitting, you agree to our{" "}
                      <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>
                    </p>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PhotographerContactModal;
